// Fonts belong to the owner of the background (Ezriel, 2026-10-08): a partner's fonts (family "pf-…",
// app/routers/partners.py) can only be used on that partner's backgrounds, and Azamein's uploaded fonts
// only on Azamein backgrounds. The built-in Google fonts work everywhere.
import { TEXT_DESIGNS } from './textDesigns';

export const PARTNER_FONT_PREFIX = 'pf-';
const SYMBOLS_FONT = 'Noto Sans Symbols 2';

const isPartnerFamily = (family = '') => family.startsWith(PARTNER_FONT_PREFIX);

/** The partner fonts a template carries ([{name, font_family, font_url}]), [] for Azamein templates. */
export const partnerFontsOf = (template) =>
  template?.partner_id && Array.isArray(template.fonts) ? template.fonts.filter((f) => f?.font_family) : [];

/**
 * The picker fonts allowed on `template` (picker format {family, label, uploaded, ...}). The template's own
 * partner fonts are added even if the site list hasn't loaded them yet.
 */
export const fontsForBackground = (fonts, template) => {
  const own = partnerFontsOf(template);
  const ownSet = new Set(own.map((f) => f.font_family));
  const partner = Boolean(template?.partner_id);
  const allowed = fonts.filter((f) => {
    if (!f.uploaded) return true; // built-in
    if (isPartnerFamily(f.family)) return ownSet.has(f.family);
    return !partner; // Azamein's uploaded fonts: Azamein backgrounds only
  });
  const listed = new Set(allowed.map((f) => f.family));
  const missing = own.filter((f) => !listed.has(f.font_family)).map((f) => ({ family: f.font_family, label: f.name || f.font_family, uploaded: true }));
  return [...allowed, ...missing];
};

/** The template's own font for the heading (biggest line) and for the rest (most used), if allowed. */
const ownFonts = (template, allowed) => {
  const fields = (template?.text_fields || []).filter((f) => f.font_family && allowed.has(f.font_family) && !f.hidden);
  const head = fields.reduce((b, f) => (!b || (f.font_size || 0) > (b.font_size || 0) ? f : b), null);
  const counts = {};
  fields.forEach((f) => {
    counts[f.font_family] = (counts[f.font_family] || 0) + 1;
  });
  const body = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
  return { head: head?.font_family, headSize: head?.font_size || 0, body };
};

/**
 * Switching to `template`: text lines in a font that isn't allowed there get that background's own fonts
 * (the matching line's font, else its heading / body font, else a built-in). Returns {fields, changed}.
 */
export const refontForBackground = (fields, template, allowedFamilies) => {
  const own = ownFonts(template, allowedFamilies);
  const byId = Object.fromEntries((template?.text_fields || []).map((f) => [f.id, f]));
  let changed = 0;
  const next = fields.map((f) => {
    if (!f.font_family || f.font_family === SYMBOLS_FONT || allowedFamilies.has(f.font_family)) return f;
    changed += 1;
    const same = byId[f.id]?.font_family;
    const big = own.headSize && (f.font_size || 0) >= own.headSize * 0.85;
    const pick = [same, big ? own.head : own.body, own.body, own.head].find((x) => x && allowedFamilies.has(x)) || 'Frank Ruhl Libre';
    return { ...f, font_family: pick };
  });
  return { fields: changed ? next : fields, changed };
};

const designsCache = new WeakMap(); // same template object -> same array (keeps the panel's previews memoized)

/**
 * Ready text designs for `template`. On a partner background the designs use that partner's fonts
 * (first font for headings and names, second for the rest) and otherwise only built-in fonts, never
 * Azamein's own uploaded fonts.
 */
export const textDesignsFor = (template) => {
  const own = partnerFontsOf(template).map((f) => f.font_family);
  if (!template?.partner_id || !own.length) return TEXT_DESIGNS;
  if (designsCache.has(template)) return designsCache.get(template);
  const [first, second = first] = own;
  const lead = { head: first, week: first, name: first, body: second, small: second };
  const designs = TEXT_DESIGNS.map((d) => ({
    ...d,
    fonts: Object.fromEntries(
      Object.entries(d.fonts).map(([slot, list]) => [slot, lead[slot] ? [lead[slot], ...list] : list])
    ),
  }));
  designsCache.set(template, designs);
  return designs;
};
